// ─── Smartguides published-context store (f-00203 Phase 3) ───────────────────
// Owns the single snapshot pcp-apps publishes to the self-embedded Smartguides
// player via `window.PCPGuides.context(...)` (the Phase-2 bridge, REPLACE semantics
// — each call is a full snapshot). Two producers feed it:
//   • RouteTracker (App.tsx) sets `page` + the entity SKELETON ({type,id}) from the
//     URL on every navigation.
//   • Entity detail pages ENRICH `entity.*` with the record's real fields once their
//     data loads ({{ entity.title }}, {{ entity.status }}, …).
// Because the bridge is REPLACE, both must go through here so neither clobbers the
// other. Imperative (no React state) — callers are effects; publishing just writes
// to the player global. Never throws into navigation; no-op when the player is absent.

interface GuidesPage {
  route: string;
  path: string;
  search: string;
  title: string;
}
interface GuidesEntity {
  type: string;
  id: string;
  [k: string]: unknown;
}

let page: GuidesPage | null = null;
let entity: GuidesEntity | null = null;
// A detail page's enrich can arrive BEFORE RouteTracker seeds the skeleton (child
// effects run before ancestor effects — notably on a react-query cache hit, where the
// enrich effect fires immediately and won't re-run for stable cached data). Buffer it
// here keyed by id and apply it when the matching skeleton is set, so enrichment is
// never lost to effect ordering.
let pendingEnrich: { id: string; fields: Record<string, unknown> } | null = null;

function publish(): void {
  try {
    const api = (window as unknown as { PCPGuides?: { context?: (c: unknown) => void } }).PCPGuides;
    if (!api?.context) return;
    api.context({ ...(page ? { page } : {}), ...(entity ? { entity } : {}) });
  } catch {
    /* never block navigation on context publishing */
  }
}

/** The lightweight entity hint from a detail-route URL ({ type, id }); undefined for
 *  non-detail routes. Kept here as the single source of the entity-route contract. */
export function entitySkeletonFromPath(pathname: string): { type: string; id: string } | undefined {
  const m = /^\/(backlog|requests|topics|tickets|guides)\/([^/?#]+)/.exec(pathname);
  if (!m) return undefined;
  let id = m[2];
  try {
    id = decodeURIComponent(m[2]);
  } catch {
    /* keep the raw segment */
  }
  return { type: m[1], id };
}

/**
 * RouteTracker → set the page, and (re)seed the entity skeleton from the URL.
 * The entity is RESET to the skeleton only when the record actually changed
 * (id/type differ) — so the debounced title re-publish (same record) PRESERVES any
 * enrichment a detail page already merged. A non-detail route clears the entity.
 */
export function setGuidesRoute(nextPage: GuidesPage, skeleton?: { type: string; id: string }): void {
  page = nextPage;
  if (!skeleton) {
    entity = null;
    pendingEnrich = null; // navigated to a non-detail route — drop any buffered enrich
  } else {
    if (!entity || entity.type !== skeleton.type || entity.id !== skeleton.id) {
      entity = { type: skeleton.type, id: skeleton.id };
    } // same record → keep the enriched fields
    if (pendingEnrich) {
      // apply a buffered enrich that arrived before this skeleton; drop a stale one
      if (pendingEnrich.id === skeleton.id) entity = { ...entity, ...pendingEnrich.fields };
      pendingEnrich = null;
    }
  }
  publish();
}

/**
 * Entity detail page → merge the record's real fields into `entity.*`, but ONLY when
 * it's still the current record (guards a late/async enrich from a page the viewer
 * already navigated away from). Undefined values are dropped so a still-loading field
 * never publishes `undefined`.
 */
export function enrichGuidesEntity(id: string, fields: Record<string, unknown>): void {
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    // `type` + `id` are owned by the URL skeleton — a detail page must NOT overwrite
    // them (e.g. a request's own `type: 'feature'` would clobber the namespace
    // `type: 'requests'`, and the next same-record re-publish would then see a
    // "different" record and reset, dropping all enrichment). Use `kind` for the
    // record's own type.
    if (k === 'type' || k === 'id') continue;
    if (v !== undefined && v !== null && v !== '') clean[k] = v;
  }
  if (!Object.keys(clean).length) return;
  if (entity && entity.id === id) {
    entity = { ...entity, ...clean };
    pendingEnrich = null;
    publish();
  } else {
    // Skeleton not set yet (effect ordering) — buffer, accumulating across calls for
    // the same id; setGuidesRoute applies it when the matching skeleton arrives.
    pendingEnrich = { id, fields: { ...(pendingEnrich?.id === id ? pendingEnrich.fields : {}), ...clean } };
  }
}
